import * as yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Schema } from 'yup';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getKivra } from 'src/shared/services/invoiceGroupService';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { Alert, Grid } from '@mui/material';
import { KivraTenantResponse } from '../models/kivraTenantResponse';

type KivraConnectEditModalProps = {
  kivraConnectEditModal: boolean;
  setKivraConnectEditModal: (onClose: boolean) => void;
  invoiceGroupId: number;
};

export const KivraConnectEditModal = (props: KivraConnectEditModalProps) => {
  const { kivraConnectEditModal, setKivraConnectEditModal, invoiceGroupId } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);

  const schema: Schema<KivraTenantResponse> = yup.object().shape({
    tenantKey: yup.string().required(),
    name: yup.string().required(),
    location: yup.string().optional(),
    companyID: yup
      .array()
      .of(
        yup.object({
          name: yup.string().required(),
          orgNr: yup.string().required(),
        }),
      )
      .required(),
    editSecurityLevel: yup.number().required(),
    groups: yup.array().optional(),
    visibility: yup.string().required(),
    createdAt: yup.string().required(),
    createdBy: yup.string().required(),
    status: yup.string().required(),
    class: yup.string().required(),
  });

  const formMethods = useForm<KivraTenantResponse>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      tenantKey: '',
      name: '',
      location: '',
      companyID: [{ name: '', orgNr: '' }],
      editSecurityLevel: 0,
      groups: [],
      visibility: '',
      createdAt: '',
      createdBy: '',
      status: '',
      class: '',
    },
  });

  const { handleSubmit, reset, control } = formMethods;

  const { fields } = useFieldArray({
    control,
    name: 'companyID',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getKivra(invoiceGroupId);
      if (isMounted()) {
        reset(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [invoiceGroupId, isMounted, reset]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onSubmit = async (data: KivraTenantResponse) => {
    ConsoleHelper.log('data: ', data);
  };

  return (
    <GenericDialog
      open={kivraConnectEditModal}
      onClose={() => setKivraConnectEditModal(false)}
      // onSave={handleSubmit(onSubmit)}
      onSave={() => {
        ConsoleHelper.log();
      }}
      maxWidth='sm'
      dialogTitle={t('invoice:adminInvoiceGroupDetailPage.kivraConnectionTitle')}
      dialogContent={
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3} p={0}>
              <Grid item xs={12}>
                <Alert
                  icon={false}
                  severity='info'
                  sx={{
                    '& .MuiAlert-message': { width: '100% !important' },
                    fontWeight: 500,
                    m: -2,
                  }}
                >
                  {t('invoice:adminInvoiceGroupDetailPage.kivraConnectionInfo')}
                </Alert>
              </Grid>
              <LoadingOverlay loading={loading} />
              <Grid item xs={12} mt={2}>
                <ControlledTextField disabled label={t('common:name')} name='name' />
              </Grid>
              {fields.map((field, index) => (
                <Grid item xs={12} key={field.id}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <ControlledTextField
                        disabled
                        label={t('invoice:adminInvoiceGroupDetailPage.legalCompanyName')}
                        name={`companyID.${index}.name`}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField disabled label={t('invoice:adminInvoiceGroupDetailPage.orgNr')} name={`companyID.${index}.orgNr`} />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </form>
        </FormProvider>
      }
      showDialogActions={false}
    />
  );
};
